import React, { useEffect, useMemo, useRef, useState } from "react";
import firebase from "firebase/compat/app";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import { Delete } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import MuiAlert from "@mui/material/Alert";
import {
  Button,
  Modal,
  TextField,
  FormControl,
  Box,
  Fab,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  IconButton,
  InputAdornment,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import InfiniteScrollSelect from "components/shared/InfiniteScrollSelect";
import useCustomParams from "./shared/useCustomParams";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const CHUNK = 20;

export const Inspections = ({ userId, projectId, contacts, isInactive }) => {
  const [openModal, setOpenModal] = useState(false);
  const [newInspection, setNewInspection] = useState("");
  const [dateSent, setDateSent] = useState("");
  const [dateRecieved, setDateRecieved] = useState("");
  const [inspectorName, setInspectorName] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [currentInspection, setCurrentInspection] = useState(null);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Server pagination state
  const { params, updateParams } = useCustomParams();
  const [page, setPage] = useState(Number(params.get("page")) || 0);
  const [pageSize, setPageSize] = useState(Number(params.get("pageSize")) || 50);
  const [loading, setLoading] = useState(false);
  const [rowsData, setRowsData] = useState([]); // raw docs from Firestore for the current page
  const [rowCount, setRowCount] = useState(undefined); // Optional: set if you maintain a counter elsewhere
  const cursorsRef = useRef([]); // cursorsRef.current[page] = lastDoc for that page
  const [invoiceCache, setInvoiceCache] = useState({}); // { invoiceId: invoiceNumberLabel }

  // ---------- Helpers ----------
  const baseQuery = useMemo(() => {
    return firebase
      .firestore()
      .collection("inspections")
      .where("userId", "==", userId)
      .where("projectId", "==", projectId)
      .orderBy("dateCreated", "desc");
  }, [userId, projectId]);

  const fetchInvoicesByIds = async (ids) => {
    if (!ids.length) return {};
    // chunk "in" queries
    const chunks = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      chunks.push(ids.slice(i, i + CHUNK));
    }
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const snap = await firebase
          .firestore()
          .collection("invoices")
          .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
          .get();
        const map = {};
        snap.forEach((doc) => {
          const data = doc.data() || {};
          map[doc.id] = data.invoiceNumber || "";
        });
        return map;
      })
    );
    return Object.assign({}, ...results);
  };

  const ensureCursorForPage = async (targetPage, size) => {
    let have = cursorsRef.current[targetPage - 1];
    if (targetPage === 0 || have) return;

    let startFrom = 0;
    while (startFrom < targetPage && !cursorsRef.current[startFrom - 1]) {
      startFrom++;
      if (startFrom === targetPage) break;
    }
    // rebuild cursors from 0 up to targetPage-1
    let lastDoc = null;
    for (let p = 0; p < targetPage; p++) {
      let q = baseQuery.limit(size);
      if (p > 0) {
        const prev = cursorsRef.current[p - 1] || lastDoc;
        if (!prev) break;
        q = q.startAfter(prev);
      }
      const snap = await q.get();
      lastDoc = snap.docs[snap.docs.length - 1] || null;
      cursorsRef.current[p] = lastDoc; // cursor for next page
    }
  };

const fetchTotalCount = async () => {
  try {
    const agg = await baseQuery.count().get();
    setRowCount(agg.data().count || 0);
  } catch (e) {
    console.error("count() failed; will infer from pages", e);
    setRowCount(undefined);   // <-- not 0
  }
};


const loadPage = async (nextPage, nextPageSize) => {
  setLoading(true);
  try {
    await ensureCursorForPage(nextPage, nextPageSize);

    let q = baseQuery.limit(nextPageSize);
    if (nextPage > 0) {
      const cursor = cursorsRef.current[nextPage - 1];
      if (cursor) q = q.startAfter(cursor);
    }

    const snap = await q.get();
    const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    cursorsRef.current[nextPage] = snap.docs[snap.docs.length - 1] || null;

    setRowsData(docs);

    if (typeof rowCount !== "number" && docs.length < nextPageSize) {
      setRowCount(nextPage * nextPageSize + docs.length);
    }

    const needed = Array.from(new Set(
      docs.map(d => d.invoiceNumber).filter(Boolean).filter(id => !invoiceCache[id])
    ));
    if (needed.length) {
      const fetched = await fetchInvoicesByIds(needed);
      setInvoiceCache(prev => ({ ...prev, ...fetched }));
    }
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    if (!userId || !projectId) return;
    cursorsRef.current = [];
    setPage(0);
    fetchTotalCount();
    loadPage(0, pageSize);
  }, [userId, projectId]);


  const currentTimestamp = Date.now();

  const handleOpenModal = () => {
    setNewInspection("");
    setDateSent("");
    setDateRecieved("");
    setInspectorName("");
    setInvoiceNumber("");
    setOpenModal(true);
  };
  const handleCloseModal = () => setOpenModal(false);

const handleAddInspection = async () => {
  try {
    const docRef = await firebase.firestore().collection("inspections").add({
      inspection: newInspection,
      dateSent,
      dateRecieved,
      inspectorName,
      invoiceNumber,
      userId,
      projectId,
      dateCreated: Date.now(),
      dateUpdated: Date.now(),
    });

    // reset form + toast
    setNewInspection(""); setDateSent(""); setDateRecieved("");
    setInspectorName(""); setInvoiceNumber("");
    handleCloseModal();
    openFeedbackMessage("Inspection added successfully!", "success");

    // refresh data shown in the grid
    cursorsRef.current = [];
    setPage(0);
    await fetchTotalCount();          // keeps footer correct
    await loadPage(0, pageSize);      // <-- this actually repaints the grid

  } catch (error) {
    console.error("Error adding inspection:", error);
  }
};

  const handleUpdateInspection = async () => {
    try {
      const {
        inspection,
        dateSent,
        dateRecieved,
        inspectorName,
        invoiceNumber,
      } = currentInspection;

      await firebase
        .firestore()
        .collection("inspections")
        .doc(currentInspection.id)
        .update({
          inspection,
          dateSent,
          dateRecieved,
          inspectorName,
          invoiceNumber,
          dateUpdated: currentTimestamp,
        });

      handleCloseUpdateModal();
      openFeedbackMessage("Inspection updated successfully!", "success");
      loadPage(page, pageSize);
    } catch (error) {
      console.error("Error updating inspection:", error);
    }
  };

  const handleCloseUpdateModal = () => setOpenUpdateModal(false);

  const editDocument = (id) => {
    const target = rowsData.find((r) => r.id === id);
    if (target) {
      setCurrentInspection(target);
      setOpenUpdateModal(true);
    }
  };

  const handleDeleteInspection = async () => {
    try {
      await firebase.firestore().collection("inspections").doc(deleteId).delete();
      await fetchTotalCount();

      setOpenConfirmDialog(false);
      setDeleteId(null);
      openFeedbackMessage("Inspection deleted successfully!", "success");
      cursorsRef.current = [];
      setPage(0);
      await loadPage(0, pageSize);
    } catch (error) {
      console.error("Error deleting inspection:", error);
    }
  };

  const confirmDeleteInspection = (id) => {
    setDeleteId(id);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setDeleteId(null);
  };

  const openFeedbackMessage = (message, severity) => {
    setFeedbackMessage({ open: true, message, severity });
  };
  const handleCloseFeedbackMessage = (_, reason) => {
    if (reason === "clickaway") return;
    setFeedbackMessage((s) => ({ ...s, open: false }));
  };

  const contactsMap = useMemo(
    () => Object.fromEntries((contacts || []).map((c) => [c.id, c.fullName])),
    [contacts]
  );

  const columns = useMemo(() => {
    const cols = [
      { field: "inspection", headerName: "Inspection", width: 150 },
      { field: "dateSent", headerName: "Date Sent", width: 150 },
      { field: "dateRecieved", headerName: "Date Recieved", width: 150 },
      { field: "inspectorName", headerName: "Inspector Name", width: 180 },
      { field: "invoiceNumber", headerName: "Invoice Number", width: 180 },
    ];
    if (!isInactive) {
      cols.push({
        field: "actions",
        headerName: "Actions",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <>
            <GridActionsCellItem
              icon={<EditIcon />}
              onClick={() => editDocument(params.row.id)}
              label="Edit"
            />
            <GridActionsCellItem
              icon={<Delete />}
              onClick={(e) => {
                e.stopPropagation();
                confirmDeleteInspection(params.row.id);
              }}
              label="Delete"
            />
          </>
        ),
      });
    }
    return cols;
  }, [isInactive]);

  const rows = useMemo(
    () =>
      rowsData.map((d) => ({
        id: d.id,
        inspection: d.inspection || "",
        dateSent: d.dateSent || "",
        dateRecieved: d.dateRecieved || "",
        inspectorName: contactsMap[d.inspectorName] || "",
        invoiceNumber: invoiceCache[d.invoiceNumber] || "",
      })),
    [rowsData, contactsMap, invoiceCache]
  );

  // ---------- Render ----------
  return (
    <div style={{ height: 500, width: "100%" }}>
      {loading && (
        <Box sx={{ position: "fixed", bottom: "2rem", right: "2rem", zIndex: 1500 }}>
          <CircularProgress />
        </Box>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        paginationMode="server"
        rowCount={rowCount ?? undefined}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page: p, pageSize: ps }) => {
          if (ps !== pageSize) cursorsRef.current = [];

          let target = p;
          if (typeof rowCount === "number") {
            const maxPage = Math.max(0, Math.ceil(rowCount / ps) - 1);
            if (target > maxPage) target = maxPage;
            if (target < 0) target = 0;
          }

          setPage(target);
          setPageSize(ps);
          updateParams({ page: target, pageSize: ps });
        }} onRowClick={(params) => editDocument(params.row.id)}
        initialState={{ pagination: { paginationModel: { pageSize } } }}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500 } },
        }}
        disableColumnSelector
        disableDensitySelector
        rowBuffer={2}
        rowThreshold={3}
      />

      <Box
        sx={{
          position: "fixed",
          bottom: "1rem",
          left: "4rem",
          "& > :not(style)": { m: 1 },
        }}
      >
        {!isInactive && (
          <Fab color="primary" aria-label="add" variant="extended" onClick={handleOpenModal}>
            <AddIcon sx={{ mr: 1 }} />
            New
          </Fab>
        )}
      </Box>

      {/* Add Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 20,
          }}
        >
          <h2>Add New Inspection</h2>

          <FormControl fullWidth>
            <TextField
              label="Inspection"
              name="inspection"
              variant="outlined"
              select
              value={newInspection || ""}
              onChange={(e) => setNewInspection(e.target.value)}
              sx={{ marginBottom: "1rem" }}
              InputProps={{
                endAdornment: newInspection ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setNewInspection("")} style={{ marginRight: "10px" }}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            >
              <MenuItem value="Sign Off HVAC">Sign Off HVAC</MenuItem>
              <MenuItem value="Sign Off FIRESTOP">Sign Off FIRESTOP</MenuItem>
              <MenuItem value="Sign Off CONST">Sign Off CONST</MenuItem>
              <MenuItem value="Sign Off WELDING">Sign Off WELDING</MenuItem>
              <MenuItem value="Sign Off BOLTS">Sign Off BOLTS</MenuItem>
              <MenuItem value="Sign Off FIRE PROOF">Sign Off FIRE PROOF</MenuItem>
              <MenuItem value="Sign Off A/C UNIT">Sign Off A/C UNIT</MenuItem>
              <MenuItem value="Sign Off ENERGY CODE OT">Sign Off ENERGY CODE OT</MenuItem>
              <MenuItem value="Sign Off ENERGY CODE MH">Sign Off ENERGY CODE MH</MenuItem>
              <MenuItem value="Sign Off SPRINKLER SYSTEMS">Sign Off SPRINKLER SYSTEMS</MenuItem>
              <MenuItem value="Sign Off LIGHTNING POWER">Sign Off LIGHTNING POWER</MenuItem>
              <MenuItem value="Sign Off LIGHTNING CONTROL">Sign Off LIGHTNING CONTROL</MenuItem>
              <MenuItem value="Sign Off EXIT SIGNS">Sign Off EXIT SIGNS</MenuItem>
              <MenuItem value="Sign Off STRUCTURAL STABILITY">Sign Off STRUCTURAL STABILITY</MenuItem>
              <MenuItem value="Sign Off CAST IN PLACE CONCRETE">Sign Off CAST IN PLACE CONCRETE</MenuItem>
              <MenuItem value="Sign Off DUCT PLENUM & PIPING">Sign Off DUCT PLENUM & PIPING</MenuItem>
            </TextField>
          </FormControl>

          <TextField
            label="Date sent"
            value={dateSent}
            onChange={(e) => setDateSent(e.target.value)}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: "1rem" }}
          />

          <TextField
            label="Date Recieved"
            value={dateRecieved}
            onChange={(e) => setDateRecieved(e.target.value)}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: "1rem" }}
          />

          <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={(e) => setInspectorName(e.target.value)}
              value={inspectorName}
              name="inspectorName"
              inputLabel="Inspector Name"
            />
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
            <InfiniteScrollSelect
              collectionName="invoices"
              label="invoiceNumber"
              onChange={(e) => setInvoiceNumber(e.target.value)}
              value={invoiceNumber}
              name="invoiceNumber"
              inputLabel="Invoice Number"
            />
          </FormControl>

          <Button variant="contained" color="primary" disabled={!newInspection} onClick={handleAddInspection} sx={{ mt: 2 }}>
            Add
          </Button>
          <Button variant="contained" color="secondary" onClick={handleCloseModal} sx={{ mt: 2, ml: 2 }}>
            Cancel
          </Button>
        </div>
      </Modal>

      {/* Update Modal (unchanged logic) */}
      <Modal open={openUpdateModal} onClose={handleCloseUpdateModal}>
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            backgroundColor: "white",
            padding: 20,
          }}
        >
          <h2>Update Inspection</h2>

          <FormControl fullWidth>
            <TextField
              label="Inspection"
              name="inspection"
              variant="outlined"
              select
              value={currentInspection?.inspection || ""}
              onChange={(e) => setCurrentInspection((prev) => ({ ...prev, inspection: e.target.value }))}
              sx={{ marginBottom: "1rem" }}
              InputProps={{
                endAdornment: currentInspection?.inspection ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setCurrentInspection((prev) => ({ ...prev, inspection: "" }))} style={{ marginRight: "10px" }}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            >
              {/* same menu items as add */}
              <MenuItem value="Sign Off HVAC">Sign Off HVAC</MenuItem>
              <MenuItem value="Sign Off FIRESTOP">Sign Off FIRESTOP</MenuItem>
              <MenuItem value="Sign Off CONST">Sign Off CONST</MenuItem>
              <MenuItem value="Sign Off WELDING">Sign Off WELDING</MenuItem>
              <MenuItem value="Sign Off BOLTS">Sign Off BOLTS</MenuItem>
              <MenuItem value="Sign Off FIRE PROOF">Sign Off FIRE PROOF</MenuItem>
              <MenuItem value="Sign Off A/C UNIT">Sign Off A/C UNIT</MenuItem>
              <MenuItem value="Sign Off ENERGY CODE OT">Sign Off ENERGY CODE OT</MenuItem>
              <MenuItem value="Sign Off ENERGY CODE MH">Sign Off ENERGY CODE MH</MenuItem>
              <MenuItem value="Sign Off SPRINKLER SYSTEMS">Sign Off SPRINKLER SYSTEMS</MenuItem>
              <MenuItem value="Sign Off LIGHTNING POWER">Sign Off LIGHTNING POWER</MenuItem>
              <MenuItem value="Sign Off LIGHTNING CONTROL">Sign Off LIGHTNING CONTROL</MenuItem>
              <MenuItem value="Sign Off EXIT SIGNS">Sign Off EXIT SIGNS</MenuItem>
              <MenuItem value="Sign Off STRUCTURAL STABILITY">Sign Off STRUCTURAL STABILITY</MenuItem>
              <MenuItem value="Sign Off CAST IN PLACE CONCRETE">Sign Off CAST IN PLACE CONCRETE</MenuItem>
              <MenuItem value="Sign Off DUCT PLENUM & PIPING">Sign Off DUCT PLENUM & PIPING</MenuItem>
            </TextField>
          </FormControl>

          <TextField
            label="Date sent"
            value={currentInspection?.dateSent || ""}
            onChange={(e) => setCurrentInspection((prev) => ({ ...prev, dateSent: e.target.value }))}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: "1rem" }}
          />

          <TextField
            label="Date Recieved"
            value={currentInspection?.dateRecieved || ""}
            onChange={(e) => setCurrentInspection((prev) => ({ ...prev, dateRecieved: e.target.value }))}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            sx={{ marginBottom: "1rem" }}
          />

          <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
            <InfiniteScrollSelect
              collectionName="contacts"
              label="fullName"
              onChange={(e) => setCurrentInspection((prev) => ({ ...prev, inspectorName: e.target.value }))}
              value={currentInspection?.inspectorName || ""}
              name="inspectorName"
              inputLabel="Inspector Name"
            />
          </FormControl>

          <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
            <InfiniteScrollSelect
              collectionName="invoices"
              label="invoiceNumber"
              onChange={(e) => setCurrentInspection((prev) => ({ ...prev, invoiceNumber: e.target.value }))}
              value={currentInspection?.invoiceNumber || ""}
              name="invoiceNumber"
              inputLabel="Invoice Number"
            />
          </FormControl>

          {!isInactive && (
            <Button
              variant="contained"
              color="primary"
              disabled={!currentInspection?.inspection}
              onClick={handleUpdateInspection}
              sx={{ mt: 2 }}
            >
              Save
            </Button>
          )}
          <Button variant="contained" color="secondary" onClick={handleCloseUpdateModal} sx={{ mt: 2, ml: 2 }}>
            Close
          </Button>
        </div>
      </Modal>

      {/* Confirm Delete */}
      <Dialog open={openConfirmDialog} onClose={handleCloseConfirmDialog}>
        <DialogTitle>Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this inspection? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteInspection} variant="contained">
            Delete
          </Button>
          <Button onClick={handleCloseConfirmDialog} variant="contained" color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar open={feedbackMessage.open} autoHideDuration={2000} onClose={handleCloseFeedbackMessage}>
        <Alert onClose={handleCloseFeedbackMessage} severity={feedbackMessage.severity} sx={{ width: "100%" }}>
          {feedbackMessage.message}
        </Alert>
      </Snackbar>
    </div>
  );
};
